export const TRANSITIONS = ['off', 'dip', 'flash'] as const;
export type Transition = typeof TRANSITIONS[number];
export const TRANSITION_LABELS: Record<Transition, string> = { off: 'Без перехода', dip: 'Затемнение', flash: 'Вспышка' };
export const TRANSITION_MIN_S = 0.12;
export const TRANSITION_MAX_S = 0.6;
export interface TransitionWindow { start: number; cut: number; end: number }

/** Frame-aligned visual windows. The audio and timeline are never overlapped or shortened. */
export function transitionWindows(
  timeline: readonly { src_in: number; src_out: number; asset_id?: string }[],
  durationS = 0.3, fps = 30, speed = 1,
): TransitionWindow[] {
  const duration = Math.max(TRANSITION_MIN_S, Math.min(TRANSITION_MAX_S, Number.isFinite(durationS) ? durationS : 0.3));
  const windows: TransitionWindow[] = [];
  let elapsed = 0;
  for (let i = 0; i + 1 < timeline.length; i++) {
    const before = timeline[i]!, after = timeline[i + 1]!;
    const previousEnd = Math.round(elapsed * fps);
    elapsed += Math.max(0, before.src_out - before.src_in) / speed;
    if (before.asset_id === after.asset_id && Math.abs(before.src_out - after.src_in) < 0.002) continue;
    const cutFrame = Math.round(elapsed * fps);
    const nextEnd = Math.round((elapsed + Math.max(0, after.src_out - after.src_in) / speed) * fps);
    const halfFrames = Math.floor(Math.min(duration * fps / 2, (cutFrame - previousEnd) / 2, (nextEnd - cutFrame) / 2));
    if (halfFrames < 1) continue;
    windows.push({ start: (cutFrame - halfFrames) / fps, cut: cutFrame / fps, end: (cutFrame + halfFrames) / fps });
  }
  return windows;
}

export function transitionOpacity(time: number, window: TransitionWindow): number {
  if (time < window.start || time > window.end) return 0;
  return Math.max(0, Math.min(1, time <= window.cut
    ? (time - window.start) / (window.cut - window.start)
    : (window.end - time) / (window.end - window.cut)));
}

/** FFmpeg fade uses the same linear envelope as the browser preview. */
export function transitionFilter(type: Transition | undefined, windows: TransitionWindow[]): string | null {
  if (!type || type === 'off' || !windows.length) return null;
  const color = type === 'flash' ? 'white' : 'black';
  const f = (n: number) => n.toFixed(6);
  return windows.flatMap(w => [
    `fade=t=out:st=${f(w.start)}:d=${f(w.cut - w.start)}:color=${color}:enable='between(t,${f(w.start)},${f(w.cut)})'`,
    `fade=t=in:st=${f(w.cut)}:d=${f(w.end - w.cut)}:color=${color}:enable='between(t,${f(w.cut)},${f(w.end)})'`,
  ]).join(',');
}
